import React from 'react';
import PropTypes from 'prop-types';
import MarketEnergyAllocation from './MarketEnergyAllocation/MarketEnergyAllocation';
import OptimalBiddingTable from './OptimalBiddingTable/OptimalBiddingTable';
import config from './commercial-optimization-config.js';
import './commercial-optimization.css';
import { 
  useCommercialOptimizationData, 
  useCommercialOptimizationState,
  useResponsiveLayout 
} from './useCommercialOptimization';
import { formatTimestamp, formatPercentage } from './commercial-optimization-utils';

/**
 * Commercial Optimization Widget
 * A configurable component for displaying market energy allocation and optimal bidding strategies
 */
const CommercialOptimization = ({ 
  className = "",
  onRiskChange = null,
  onDataUpdate = null,
  enableAutoRefresh = true,
  showPerformanceMetrics = false
}) => {
  // Custom hooks for data and state management
  const {
    selectedRisk,
    handleRiskChange,
    handleRowSelection,
    selectedRow
  } = useCommercialOptimizationState(config?.controls?.defaultRisk || 'Medium Risk');

  const {
    marketData,
    biddingData,
    riskMetrics,
    isLoading,
    error,
    lastUpdated,
    retry,
    refresh
  } = useCommercialOptimizationData(config, selectedRisk);

  const { isMobile, isTablet } = useResponsiveLayout();

  // Handle risk level change
  const handleRiskLevelChange = (event) => {
    const newRisk = event.target.value;
    handleRiskChange(newRisk);
    
    if (onRiskChange) {
      onRiskChange(newRisk);
    }
  };

  // Handle data updates from child components
  const handleChildDataUpdate = (data, type) => {
    if (onDataUpdate) {
      onDataUpdate({ data, type, selectedRisk });
    }
  };

  // Calculate expected revenue uplift
  const expectedRevenueUplift = riskMetrics 
    ? formatPercentage(riskMetrics.profitMargin) 
    : '24.5%';

  // Early return if config is not loaded
  if (!config || !config.layout || !config.typography) {
    return (
      <div className={`commercial-opt-container ${className}`}>
        <div className="commercial-opt-loading">
          <div className="commercial-opt-loading-spinner">⟳</div>
          Loading configuration...
        </div>
      </div>
    );
  }

  return (
    <div className={`optimal-bidding-container ${className}`}>
      {/* Header */}
      <h3 style={config.typography?.title}>
        {config.layout?.title || 'Commercial Optimization'}
      </h3>
      
      {/* Controls */}
      <div className="controls">
        <label>
          Risk Level:
          <select
            value={selectedRisk}
            onChange={handleRiskLevelChange}
            className="risk-select"
            disabled={isLoading}
            aria-label="Select risk level for analysis"
          >
            {(config.controls?.riskLevels || ['All', 'Low Risk', 'Medium Risk', 'High Risk']).map((risk) => (
              <option key={risk} value={risk}>
                {risk}
              </option>
            ))}
          </select>
        </label>
        
        <span className="revenue-uplift">Expected Revenue Uplift: {expectedRevenueUplift}</span>
        
        {!isMobile && (
          <button 
            onClick={refresh}
            disabled={isLoading}
            style={{ marginLeft: '15px', padding: '5px 10px' }}
            aria-label="Refresh data"
          >
            ⟳ Refresh
          </button>
        )}
      </div>

      {/* Content */}
      <div className={`content ${isMobile ? 'mobile' : ''}`} style={{ 
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '20px' : '40px'
      }}>
        {/* Chart */}
        <div className="chart-container">
          <h4 style={config.typography?.sectionTitle}>
            {config.marketEnergyAllocation?.title || 'Market Energy Allocation'}
          </h4>
          
          <MarketEnergyAllocation
            selectedRisk={selectedRisk}
            data={marketData}
            isLoading={isLoading}
            error={error}
            onRetry={retry}
            onDataUpdate={(data) => handleChildDataUpdate(data, 'market')}
          />
          
          {lastUpdated && !isLoading && (
            <div className="update-info">
              <i>Last updated {formatTimestamp(lastUpdated, { 
                dateStyle: undefined, 
                timeStyle: 'short' 
              })}</i>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="table-container">
          <h4 style={config.typography?.sectionTitle}>
            {config.optimalBiddingTable?.title || 'Optimal Bidding Table'}
          </h4>
          
          <OptimalBiddingTable
            selectedRisk={selectedRisk}
            data={biddingData}
            isLoading={isLoading}
            error={error}
            onRetry={retry}
            onRowClick={handleRowSelection}
            onDataUpdate={(data) => handleChildDataUpdate(data, 'bidding')}
          />
          
          {lastUpdated && !isLoading && (
            <div className="update-info">
              <i>Last updated {formatTimestamp(lastUpdated, { 
                dateStyle: undefined, 
                timeStyle: 'short' 
              })}</i>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// PropTypes for development
CommercialOptimization.propTypes = {
  className: PropTypes.string,
  onRiskChange: PropTypes.func,
  onDataUpdate: PropTypes.func,
  enableAutoRefresh: PropTypes.bool,
  showPerformanceMetrics: PropTypes.bool
};

// Default props
CommercialOptimization.defaultProps = {
  className: "",
  onRiskChange: null,
  onDataUpdate: null,
  enableAutoRefresh: true,
  showPerformanceMetrics: false
};

export default React.memo(CommercialOptimization);
